import { useState } from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createLead } from '@/services/api';

const TOUR_DAYS = Array.from({ length: 5 }).map((_, i) => {
  const d = new Date();
  d.setDate(d.getDate() + i + 1);
  return { key: d.toISOString().slice(0, 10), weekday: d.toLocaleDateString('en-US', { weekday: 'short' }), day: d.getDate() };
});

export const ContactAgentCard = ({ listing }) => {
  const [tour, setTour] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: `I'm interested in ${listing.address}. Please send more info.`,
    company: '',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast('Please add your name and email'); return; }
    setSubmitting(true);
    try {
      await createLead({
        full_name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        lead_type: tour ? 'showing' : 'contact',
        tour_date: tour || null,
        listing_mls_number: listing.mlsNumber || listing.id,
        company: form.company || '',
      });
      toast('Message sent', {
        description: tour ? `Tour requested for ${tour}. We'll be in touch shortly.` : "We'll be in touch shortly.",
      });
      setForm((f) => ({ ...f, message: '' }));
    } catch {
      toast.error('Something went wrong, please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5 shadow-md" data-testid="listing-contact-card">
      <div className="mt-0">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Calendar size={15} /> Request a tour</p>
        <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
          {TOUR_DAYS.map((d) => (
            <button
              type="button"
              key={d.key}
              onClick={() => setTour(tour === d.key ? null : d.key)}
              aria-pressed={tour === d.key}
              className={cn(
                'flex min-h-[44px] min-w-[44px] flex-col items-center justify-center rounded-xl border px-3 py-2.5 text-xs',
                tour === d.key ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-blue-300'
              )}
              data-testid={`tour-day-${d.key}`}
            >
              <span>{d.weekday}</span><span className="text-base font-semibold leading-none">{d.day}</span>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="contact-agent-form">
        <div><Label htmlFor="c-name" className="sr-only">Name</Label><Input id="c-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="contact-name" /></div>
        <div><Label htmlFor="c-email" className="sr-only">Email</Label><Input id="c-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" data-testid="contact-email" /></div>
        <div><Label htmlFor="c-phone" className="sr-only">Phone</Label><Input id="c-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" data-testid="contact-phone" /></div>
        <div><Label htmlFor="c-msg" className="sr-only">Message</Label><Textarea id="c-msg" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl" data-testid="contact-message" /></div>
        {/* Honeypot — bots fill this, humans don't */}
        <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
          <input tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
        </div>
        <Button type="submit" className="w-full rounded-xl" disabled={submitting} data-testid="contact-submit">
          {submitting ? 'Sending…' : 'Contact agent'}
        </Button>
      </form>
    </div>
  );
};
